import Common from '@/libs/common';
import type { Bounds } from '@/stores/gtfs';

/** IGN Géoplateforme geocoding (Base Adresse Nationale): free, keyless, France only. */
const GEOCODING_URL = 'https://data.geopf.fr/geocodage/search';
const REVERSE_GEOCODING_URL = 'https://data.geopf.fr/geocodage/reverse';
const GEOCODING_LIMIT = 10;
const GEOCODING_TIMEOUT_MS = 8000;
/** The service ignores a bounding box: the addresses too far from the network are dropped here. */
const NETWORK_MARGIN_DEG = 0.05;

export interface AddressResult {
  id: string;
  /** Full label, e.g. « 12 Boulevard de la Gare 29300 Quimperlé ». */
  label: string;
  /** Street part, e.g. « 12 Boulevard de la Gare ». */
  name: string;
  /** « 29300 Quimperlé ». */
  locality: string;
  lat: number;
  lon: number;
}

interface GeocodingFeature {
  geometry: { coordinates: [number, number] };
  properties: { id?: string; label: string; name?: string; postcode?: string; city?: string };
}

interface GeocodingResponse {
  features?: GeocodingFeature[];
}

export function parseGeocodingResponse(body: GeocodingResponse): AddressResult[] {
  return (body.features ?? []).map(({ geometry, properties }) => ({
    id: properties.id ?? properties.label,
    label: properties.label,
    name: properties.name ?? properties.label,
    locality: [properties.postcode, properties.city].filter(Boolean).join(' '),
    lon: geometry.coordinates[0],
    lat: geometry.coordinates[1],
  }));
}

export function isAroundNetwork(address: AddressResult, bounds: Bounds): boolean {
  if (bounds.north === null || bounds.south === null || bounds.east === null || bounds.west === null) {
    return true;
  }
  return (
    address.lat <= bounds.north + NETWORK_MARGIN_DEG &&
    address.lat >= bounds.south - NETWORK_MARGIN_DEG &&
    address.lon <= bounds.east + NETWORK_MARGIN_DEG &&
    address.lon >= bounds.west - NETWORK_MARGIN_DEG
  );
}

/**
 * A request to the geocoder: the caller may abort it (a text typed past), and it gives up by
 * itself on a stalled connection, which would otherwise keep its caller waiting for minutes.
 */
async function fetchGeocoding<T>(url: string, params: URLSearchParams, signal?: AbortSignal): Promise<T> {
  // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timeout = setTimeout(abort, GEOCODING_TIMEOUT_MS);
  if (signal?.aborted) abort();
  signal?.addEventListener('abort', abort);
  try {
    const response = await fetch(`${url}?${params}`, { signal: controller.signal });
    if (!response.ok) throw new Error(`${response.status}: ${response.statusText} when trying to get ${url}`);
    return (await response.json()) as T;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

/**
 * Addresses matching the text typed: within the network's departments when known
 * (the service's only reliable geographic filter), the ones near the network first,
 * the others dropped.
 */
export async function searchAddresses(
  query: string,
  bounds: Bounds,
  departments: string[] = [],
  signal?: AbortSignal,
): Promise<AddressResult[]> {
  const params = new URLSearchParams({
    q: query,
    index: 'address',
    autocomplete: '1',
    limit: String(GEOCODING_LIMIT),
  });
  if (departments.length > 0) params.set('depcode', departments.join(','));
  if (bounds.north !== null && bounds.south !== null && bounds.east !== null && bounds.west !== null) {
    params.set('lat', ((bounds.north + bounds.south) / 2).toFixed(4));
    params.set('lon', ((bounds.east + bounds.west) / 2).toFixed(4));
  }
  const body = await fetchGeocoding<GeocodingResponse>(GEOCODING_URL, params, signal);
  return parseGeocodingResponse(body).filter(address => isAroundNetwork(address, bounds));
}

/** The address nearest to a point, as the search lists it; undefined far from any address. */
export async function nearestAddress(
  point: { lat: number; lon: number },
  signal?: AbortSignal,
): Promise<AddressResult | undefined> {
  const params = new URLSearchParams({
    lat: point.lat.toFixed(6),
    lon: point.lon.toFixed(6),
    index: 'address',
    limit: '1',
  });
  const body = await fetchGeocoding<GeocodingResponse>(REVERSE_GEOCODING_URL, params, signal);
  return parseGeocodingResponse(body)[0];
}

// #region departments of the network

interface Located {
  stop_lat: number;
  stop_lon: number;
}

interface Point {
  lat: number;
  lon: number;
}

/** About 1 km: an address that far past the outermost stops can still walk to one of them. */
const BORDER_REACH_DEG = 0.01;
const SPREAD_SAMPLES = 8;
const REVERSE_BATCH = 6;
const DEPARTMENTS_STORAGE_KEY = 'networkDepartments';
const DEPARTMENTS_STORAGE_VERSION = 1;

interface StoredDepartments {
  v: number;
  groupId: string;
  departments: string[];
}

/** The department of an INSEE commune code: « 29 » for 29233, « 974 » overseas, « 2A » in Corsica. */
export function departmentOf(citycode: string): string {
  return citycode.startsWith('97') ? citycode.slice(0, 3) : citycode.slice(0, 2);
}

/**
 * Where to ask which department the network lies in: its outermost stops, the same
 * pushed about 1 km outwards (an address across a department border is still in
 * walking range), and stops spread over the network.
 */
export function departmentSamplePoints(stops: Located[]): Point[] {
  if (stops.length === 0) return [];
  const point = (stop: Located, dLat = 0, dLon = 0): Point => ({
    lat: stop.stop_lat + dLat,
    lon: stop.stop_lon + dLon,
  });
  const extreme = (score: (stop: Located) => number) =>
    stops.reduce((best, stop) => (score(stop) > score(best) ? stop : best));
  const north = extreme(stop => stop.stop_lat);
  const south = extreme(stop => -stop.stop_lat);
  const east = extreme(stop => stop.stop_lon);
  const west = extreme(stop => -stop.stop_lon);
  const byLatitude = [...stops].sort((a, b) => a.stop_lat - b.stop_lat);
  const spread = Array.from({ length: Math.min(SPREAD_SAMPLES, stops.length) }, (_, i) =>
    point(byLatitude[Math.floor(((i + 0.5) * byLatitude.length) / Math.min(SPREAD_SAMPLES, stops.length))]),
  );
  const points = [
    point(north),
    point(south),
    point(east),
    point(west),
    point(north, BORDER_REACH_DEG),
    point(south, -BORDER_REACH_DEG),
    point(east, 0, BORDER_REACH_DEG),
    point(west, 0, -BORDER_REACH_DEG),
    ...spread,
  ];
  const seen = new Set<string>();
  return points.filter(({ lat, lon }) => {
    const key = `${lat.toFixed(3)},${lon.toFixed(3)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** The department of the address nearest to a point, or undefined at sea. */
async function departmentAt({ lat, lon }: Point): Promise<string | undefined> {
  const params = new URLSearchParams({ lat: String(lat), lon: String(lon), index: 'address', limit: '1' });
  const body = await fetchGeocoding<{ features?: Array<{ properties?: { citycode?: string } }> }>(
    REVERSE_GEOCODING_URL,
    params,
  );
  const citycode = body.features?.[0]?.properties?.citycode;
  return citycode ? departmentOf(citycode) : undefined;
}

const pendingDepartments = new Map<string, Promise<string[]>>();

/**
 * The departments the network lies in, asked once and kept in the local storage until
 * the passenger switches networks. Empty while unknown (offline, service down): the
 * search then relies on the network bounds alone, and asks again next time.
 */
export function networkDepartments(groupId: string, stops: Located[]): Promise<string[]> {
  const stored: StoredDepartments | null = Common.safeLSParse(DEPARTMENTS_STORAGE_KEY);
  if (
    stored?.v === DEPARTMENTS_STORAGE_VERSION &&
    stored.groupId === groupId &&
    Array.isArray(stored.departments) &&
    stored.departments.length > 0
  ) {
    return Promise.resolve(stored.departments);
  }
  const pending = pendingDepartments.get(groupId);
  if (pending) return pending;

  const lookup = (async () => {
    const departments = new Set<string>();
    let failed = false;
    const points = departmentSamplePoints(stops);
    for (let i = 0; i < points.length; i += REVERSE_BATCH) {
      const found = await Promise.all(
        points.slice(i, i + REVERSE_BATCH).map(point =>
          departmentAt(point).catch(() => {
            failed = true;
            return undefined;
          }),
        ),
      );
      found.forEach(department => department && departments.add(department));
    }
    // A partial answer would leave a department out of the search: only a complete one is used.
    if (failed) return [];
    const list = [...departments].sort();
    if (list.length > 0) {
      const toStore: StoredDepartments = { v: DEPARTMENTS_STORAGE_VERSION, groupId, departments: list };
      try {
        localStorage.setItem(DEPARTMENTS_STORAGE_KEY, JSON.stringify(toStore));
      } catch {
        // Storage full or disabled: the departments are asked again next time.
      }
    }
    return list;
  })().finally(() => pendingDepartments.delete(groupId));
  pendingDepartments.set(groupId, lookup);
  return lookup;
}
// #endregion
